import React, { useEffect, useRef, useState } from "react";
import { makeStyles } from "@mui/styles";
import { useDispatch, useSelector } from "react-redux";
import { Chart } from "react-google-charts";
import { Box, Button, CircularProgress, TextField } from "@mui/material";
import { fetchRuntimeFormDetails } from "../../services/FormService";
import { Form } from "react-formio";
import ContentLoader from "../../animations/ContentLoader.json";
import Lottie from "lottie-react";
import { FORM_HIDDEN_KEYS, QCR_DISCLAIMER } from "../../constants/constants";
import { INITIAL_THEME } from "../../theme";
import Colors from "../../theme/colors.json";
import ClientLogo from "../../assets/svg/Techsophy-logo.svg";
import dayjs from "dayjs";

const useStyles = makeStyles((theme) => ({
  previewMainConatiner: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
    width: "100%",
    pointerEvents: "none",
    fontSize: theme.fontSizes.SMALL,
    "& .remarks-section-formio": {
      display: "none",
    },
  },

  logoContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  section: {
    border: "1px solid grey",
  },
  flexKeyValue: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    gap: "1%",
    padding: 5,
    width: "100%",
  },
  keyStyle: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    textAlign: "center",
    border: "1px solid grey",
    width: "50%",
    fontWeight: "bold",
    borderRadius: 5,
  },
  valueStyle: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    textAlign: "center",
    border: "1px solid grey",
    width: "50%",
    borderRadius: 5,
  },
  sectionDataFlex: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-around",
  },
  proposalKeyStyle: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    textAlign: "center",
    border: "1px solid grey",
    width: "100%",
    fontWeight: "bold",
    borderRadius: 5,
  },
  proposalValueStyle: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    textAlign: "center",
    border: "1px solid grey",
    width: "100%",
    borderRadius: 5,
  },
  proposalSection: {
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    gap: "2%",
    // width: "100%",
  },
  proposalFlexKeyValue: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    gap: "1%",
    padding: 5,
    width: "49%",
  },
  tableHead: {
    backgroundColor: Colors.FORM_HEADING_BG,
    color: theme.palette.secondary.main,
    border: "1px solid #dddddd",
  },
  table: {
    borderCollapse: "collapse",
    width: "100%",
  },
  tableCell: {
    border: "1px solid #dddddd",
    textAlign: "left",
    padding: "3px",
  },
  coveragesLimitIfAnyHeader: {
    // backgroundColor: "#D3D3D3",
    border: "1px solid #dddddd",
  },
  coveragesLimitIfAnyContainer: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    // padding: "0.5%",
  },
  coverageSection: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    textAlign: "center",
    width: "50%",
  },
  limitIfAnySection: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    textAlign: "center",
    width: "49%",
  },
  chartCard: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    height: "auto",
    width: "100%",
    overflow: "hidden",
    order: "1px solid #dddddd",
  },
  chartHeading: {
    backgroundColor: theme.palette.primary.main,
    width: "fit-content",
  },
  chartHeadingText: {
    fontWeight: "bold",
    color: Colors.WHITE_LIGHT,
    padding: 3,
  },
  loader: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center !important",
    alignItems: "center !important",
  },
  commentsInsightsSection: {
    border: "1px solid #dddddd",
    marginTop: "1%",
  },
  commentsInsightsHeading: {
    backgroundColor: theme.palette.primary.main,
    color: Colors.WHITE_LIGHT,
    fontWeight: 600,
    padding: "0.5%",
  },
  commentsInsightsContent: {
    padding: "2%",
  },
  disclaimerSection: {
    border: "1px solid #dddddd",
    marginTop: "1%",
  },
  disclaimerHeading: {
    backgroundColor: theme.palette.primary.main,
    color: Colors.WHITE_LIGHT,
    fontWeight: 600,
    padding: "0.5%",
  },
  disclaimerContent: {
    padding: "2%",
  },
  placementSlipHeadingText: {
    color: theme.palette.secondary.main,
    fontSize: "2vw",
    fontWeight: 700,
    display: "flex",
    justifyContent: "center",
  },
  policyDetailsLabelSection: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
  },
  policyDetailsLabel: {
    fontWeight: 500,
  },
  tableCellHeading: {
    border: "1px solid #dddddd",
    textAlign: "left",
    fontWeight: 600,
  },
  pdfPreviewContainer: {
    display: "flex",
    justifyContent: "center",
    width: "100%",
  },
}));

const animationStyle = {
  height: "50vh",
};

const PlacementSlipPreview = (props) => {
  const { placementSlipContentRef, formOne } = props;
  const classes = useStyles();
  const dispatch = useDispatch();

  const { proposalDataRedux } = useSelector((state: any) => ({
    proposalDataRedux: state.proposalData,
  }));

  const [proposalForm, setProposalForm] = useState<any>({});
  const [rfqData, setRfqData] = useState<any>({});

  const [underwriterInfo, setUnderwriterInfo] = useState<any>({});
  const [chartData, setChartData] = useState<any>([]);

  // Get today's date
  const today = new Date();

  // Extract day, month, and year components
  const day = String(today.getDate()).padStart(2, "0");
  const month = String(today.getMonth() + 1).padStart(2, "0"); // Months are zero-based
  const year = today.getFullYear();

  function convertToTitleFormat(text) {
    if (!text) {
      return "";
    }

    return text.replace(/([A-Z])/g, " $1").replace(/^./, function (str) {
      return str.toUpperCase();
    });
  }

  function toCamelCase(inputString) {
    // Check if inputString is empty, null, or undefined
    if (!inputString || typeof inputString !== "string") {
      return ""; // Or handle this case based on your requirements
    }

    const words = inputString.split(/\s+/);

    const camelCaseWords = words.map((word, index) => {
      if (index === 0) {
        return word.toLowerCase();
      } else {
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      }
    });

    return camelCaseWords.join("");
  }

  const getUnderwriterInfo = (underwriterData) => {
    // Your provided data
    const data = JSON.parse(JSON.stringify(underwriterData));

    // Sort premiumData based on premiumAfterTax
    data?.premiumData?.sort((a, b) => a.premiumAfterTax - b.premiumAfterTax);

    // Find and remove the object with insuranceProvider: "Original"
    const originalIndex = data?.premiumData?.findIndex(
      (item) => item?.insuranceProvider === "Original"
    );
    if (originalIndex !== -1) {
      data?.premiumData?.splice(originalIndex, 1);
    }

    setUnderwriterInfo(data || {});

    const builtChartData = data?.premiumData?.map((item) => {
      return [
        item?.insuranceProvider,
        item?.premiumAfterTax,
        `color: ${INITIAL_THEME.palette.primary.main}`,
      ];
    });
    setChartData([
      ["Element", "Premium After Tax", { role: "style" }],
      ...(builtChartData || []),
    ]);
  };

  const getProposalForm = async (formId) => {
    const response: any = await fetchRuntimeFormDetails(formId);
    if (response.success) {
      const form = replaceDisableAddingRemovingRows(
        response?.data?.components || {}
      );
      setProposalForm(form);
    } else {
      setProposalForm({ display: "form", components: [] });
    }
  };

  function replaceDisableAddingRemovingRows(data) {
    if (Array.isArray(data)) {
      data.forEach(replaceDisableAddingRemovingRows);
    } else if (typeof data === "object" && data !== null) {
      Object.keys(data).forEach((key) => {
        if (key === FORM_HIDDEN_KEYS.disableAddingRemovingRows && !data[key]) {
          data[key] = true;
        }
        const isLabelOrTitle = key === "label" || key === "title";
        if (
          isLabelOrTitle &&
          data[key] === FORM_HIDDEN_KEYS.relationShipManager
        ) {
          data["hidden"] = true;
        } else if (
          isLabelOrTitle &&
          data[key] === FORM_HIDDEN_KEYS.serviceManager
        ) {
          data["hidden"] = true;
        } else if (
          isLabelOrTitle &&
          data[key] === FORM_HIDDEN_KEYS.enclosures
        ) {
          data["hidden"] = true;
        } else if (isLabelOrTitle && data[key] === FORM_HIDDEN_KEYS.remarks) {
          data["hidden"] = true;
        } else if (typeof data[key] === "object" && data[key] !== null) {
          replaceDisableAddingRemovingRows(data[key]);
        }
      });
    }
    return data;
  }

  useEffect(() => {
    getProposalForm(proposalDataRedux?.formId);
    setRfqData(proposalDataRedux?.formData || {});
    getUnderwriterInfo(proposalDataRedux?.formData?.underwriterDetails || {});
  }, []);

  return (
    <div className={classes.pdfPreviewContainer}>
      {Object.keys(proposalForm)?.length === 0 ||
      Object.keys(rfqData)?.length === 0 ||
      Object.keys(underwriterInfo)?.length === 0 ? (
        <Box className={classes.loader}>
          <Lottie
            animationData={ContentLoader}
            loop={true}
            style={animationStyle}
          />
          {/* <CircularProgress size={80} />
          ...Please Wait */}
        </Box>
      ) : null}
      {!(Object.keys(proposalForm)?.length === 0) &&
      !(Object.keys(rfqData)?.length === 0) &&
      !(Object.keys(underwriterInfo)?.length === 0) ? (
        <div>
          <div
            ref={placementSlipContentRef}
            className={classes.previewMainConatiner}
          >
            <div className={classes.placementSlipHeadingText}>
              Placement Slip
            </div>
            <div className="techshophy-logo-container-preview">
              <img
                src={sessionStorage.getItem("logoURL") || ClientLogo}
                alt="client-logo"
              />
            </div>
            <div>
              <table className={classes.table}>
                <thead className={classes.tableHead}>
                  <th colSpan={4} className={classes.tableCellHeading}>
                    Premium Payment Details & Insurance Company Information
                  </th>
                </thead>
                <tbody>
                  <tr>
                    <td className={classes.tableCell}>
                      <div className={classes.policyDetailsLabelSection}>
                        <div className={classes.policyDetailsLabel}>
                          Policy Start Date
                        </div>
                        <div>
                          {dayjs(formOne?.values?.policyStartDate).format(
                            "DD/MM/YYYY"
                          )}
                        </div>
                      </div>
                    </td>
                    <td className={classes.tableCell}>
                      <div className={classes.policyDetailsLabel}>
                        Policy End Date
                      </div>
                      <div>
                        {dayjs(formOne?.values?.policyEndDate).format(
                          "DD/MM/YYYY"
                        )}
                      </div>
                    </td>
                    <td className={classes.tableCell}>
                      <div className={classes.policyDetailsLabel}>
                        Insurer / Lead Insurer
                      </div>
                      <div>{formOne?.values?.leadInsurer}</div>
                    </td>
                  </tr>
                  <tr>
                    <td className={classes.tableCell}>
                      <div className={classes.policyDetailsLabel}>
                        Insurer Branch
                      </div>
                      <div>{formOne?.values?.insurerBranch}</div>
                    </td>
                    <td className={classes.tableCell}>
                      <div className={classes.policyDetailsLabel}>
                        Net Premium paid
                      </div>
                      <div>{formOne?.values?.netPremiumPaid}</div>
                    </td>
                    <td className={classes.tableCell}>
                      <div className={classes.policyDetailsLabel}>
                        Gross Premium
                      </div>
                      <div>{formOne?.values?.grossPremium}</div>
                    </td>
                  </tr>
                  <tr>
                    <td className={classes.tableCell}>
                      <div className={classes.policyDetailsLabel}>
                        Mode Of Payment
                      </div>
                      <div>{formOne?.values?.paymentMode}</div>
                    </td>
                    <td className={classes.tableCell}>
                      <div className={classes.policyDetailsLabel}>
                        Payment Reference Number
                      </div>
                      <div>{formOne?.values?.paymentReferenceNumber}</div>
                    </td>
                    <td className={classes.tableCell}>
                      <div className={classes.policyDetailsLabel}>
                        Payment Date
                      </div>
                      <div>
                        {dayjs(formOne?.values?.paymentDate).format(
                          "DD/MM/YYYY"
                        )}
                      </div>
                    </td>
                  </tr>
                  <tr>
                    {formOne?.values?.financierName && (
                      <td className={classes.tableCell}>
                        <div className={classes.policyDetailsLabel}>
                          Financier Name
                        </div>
                        <div>{formOne?.values?.financierName}</div>
                      </td>
                    )}
                  </tr>
                </tbody>
              </table>
            </div>
            <div>
              <table className={classes.table}>
                <thead className={classes.tableHead}></thead>
                <tbody>
                  <tr className={classes.tableHead}>
                    <th className={classes.tableCellHeading} colSpan={3}>
                      Terms
                    </th>
                  </tr>
                  <tr className={classes.coveragesLimitIfAnyHeader}>
                    <th>In-Built Covers / Add-on Covers / Conditions</th>
                    <th className={classes.tableCell}>Limit If any</th>
                    <th></th>
                  </tr>
                  {proposalDataRedux?.formData?.underwriterDetails?.underwriterData?.map(
                    (item, index) => {
                      return (
                        <tr>
                          <td className={classes.tableCell}>
                            {item?.coverage}
                          </td>
                          <td className={classes.tableCell}>
                            {item?.proposedTermsLimitIfAny}
                          </td>
                          <td className={classes.tableCell}>
                            {
                              item[
                                toCamelCase(formOne?.values?.leadInsurer) +
                                  "YesNo"
                              ]
                            }
                            {item[
                              toCamelCase(formOne?.values?.leadInsurer) +
                                "YesNo"
                            ] &&
                            item[
                              toCamelCase(formOne?.values?.leadInsurer) +
                                "LimitIfAny"
                            ]
                              ? ","
                              : ""}{" "}
                            {
                              item[
                                toCamelCase(formOne?.values?.leadInsurer) +
                                  "LimitIfAny"
                              ]
                            }
                          </td>
                        </tr>
                      );
                    }
                  )}
                </tbody>
              </table>
            </div>
            <Form
              form={proposalForm}
              submission={{ data: { ...rfqData } }}
              options={{
                readOnly: true,
                flatten: true,
                renderMode: "html",
              }}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default PlacementSlipPreview;
